import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { FormControl, Validators } from "@angular/forms";
import { select, Store } from "@ngrx/store";
import { filter, Observable, Subscription, tap } from "rxjs";
import { filterNullOrUndefined } from "src/app/@operators/filter-null-or-undefined";
import { InsurerDto, RefLanguageDto } from "src/app/api/models";
import { submitCardId, submitCardId_S4 } from "src/app/kiosk/components/contract/state/contract.actions";
import { eKioskError } from "src/app/shared/models/api/constants";
import { AppState, getState } from "src/app/state/app.state";
import { environment } from "src/environments/environment";
import { setErrorMsg } from "../../error/state/error.actions";
import { setLangToShow } from "../../language/state/language.actions";
import { setTitle } from "../../title/state/title.actions";
import { setSelectedLanguage, setSelectedInsurer, setCardIdInUrl, setCardNumber } from "../../transaction-info/state/transaction-info.actions";
import { getCardIdInUrl, getCardidIsValid, getContractHasLanguageSupported } from "../../transaction-info/state/transaction-info.selectors";

@Component({
    selector: "app-contract",
    templateUrl: "./contract.component.html",
    styleUrls: ["./contract.component.scss"],
    standalone: false
})
export class ContractComponent implements OnInit,OnDestroy {
	@ViewChild("formCardidEl") formCardidEl: ElementRef;
	private language: RefLanguageDto;
	public insurer: InsurerDto;
	private nbTry = 0;
	private maxAttempt: number;
	public $cardIdInUrl : Observable<string> = this.store.pipe(select(getCardIdInUrl));

	formCardid = new FormControl('', [Validators.pattern('[a-zA-Z0-9]*')]);
	private subs: Subscription[] = [];
	private state: AppState;
	
	constructor(private store: Store<AppState>) {
		this.subs.push(
			getState(this.store).subscribe( state => {
				this.state = state;
				this.insurer = state.transactionInfo.selectedInsurer;
				this.language = state.transactionInfo.selectedLanguage;
				this.formCardid.setValue(state.transactionInfo.cardId ?? "");
				this.maxAttempt = state.kiosk.cardIdMaxAttempt;
			})
		);
	}

	ngOnInit(): void {
		//Set title
		this.store.dispatch(
			setTitle({
				data: "TITLE_ENTER_CARD_ID",
				data2: "TITLE_ENTER_CARD_ID_INFO",
			})
		);

		//card id validation
		this.subs.push(
			this.store
				.pipe(select(getCardidIsValid))
				.pipe(filterNullOrUndefined())
				.subscribe((cardIsValid) => {
					//Card id is valid => false
					if (cardIsValid === false) {
						this.formCardid.setValue("");
						this.formCardid.setErrors({ contractInvalidSubmit: true });
						this.formCardid.markAsTouched();
						this.formCardidEl?.nativeElement?.focus();
						this.nbTry++;
						if (this.nbTry >= this.maxAttempt) {
							this.store.dispatch(setErrorMsg({ data: null, KioskError: eKioskError.CONTRACT_NOT_KNOWN }));
						}
					}
				})
		);

		this.subs.push(
			this.store
				.pipe(select(getContractHasLanguageSupported))
				.pipe(filterNullOrUndefined())
				.subscribe((contractLanguageSupportedDto) => {
					//debugger;
					if (!contractLanguageSupportedDto.isOk) {
						if (contractLanguageSupportedDto.languageSupported) {
							this.store.dispatch(setLangToShow({ data: contractLanguageSupportedDto.languageSupported.split(",") }));
							this.store.dispatch(setSelectedLanguage({ data: undefined }));
						} else {
							this.store.dispatch(setErrorMsg({ data: contractLanguageSupportedDto.errorMessage }));
						}
					}
				})
		);

		this.subs.push(
			this.store.pipe(select(getCardIdInUrl))
			.pipe(
				filter(card => card != null),
				tap( card => {
					this.formCardid.setValue(card);
					this.onClickOk();
				})
			).subscribe()
		)

		if (environment.kioskMode && window.external.ShowKeyboard) {
			window.external.ShowKeyboard();
		}
	}

	ngOnDestroy(): void {
		this.subs.forEach((_) => _.unsubscribe());
		this.store.dispatch(setCardIdInUrl({data:null}));
	}

	public clearCardId() {
		this.formCardid.setValue('');
	}
	public onClickRetour() {
		this.store.dispatch(setCardNumber({data:undefined}));
		this.store.dispatch(setSelectedInsurer(undefined));
	}

	public onClickOk() {
		if (this.formCardid.value.toString().length < 12) {
			this.formCardid.setErrors({ minlength: true });
			return;
		}

		if (this.formCardid.invalid || this.formCardid.value.toString().trim() === "") return;

		const s4Url = environment.kioskMode ? this.insurer.specificKioskURL : this.insurer.specificHomeURL;
		if (s4Url && s4Url != "") {
			console.log("Sumbit cardId with s4Url != empty")
			this.store.dispatch(
				submitCardId_S4({
					cardNumber: this.formCardid.value.toString().toUpperCase(),
					insurer: this.insurer,
					siteId: this.state.transactionInfo?.defaultSiteId,
					hospitalNo: this.state.transactionInfo.defaultHospitalNo?.toString() ?? "",
					language: this.language,
					urls4: s4Url,
				})
			);
		} else {
			//debugger;
			console.log("Sumbit cardId with s4Url == empty")
			this.store.dispatch(
				submitCardId({
					cardNumber: this.formCardid.value.toString().toUpperCase(),
					insurer: this.insurer,
					siteId: this.state.transactionInfo.defaultSiteId,
					hospitalNo: this.state.transactionInfo.defaultHospitalNo?.toString() ?? "",
					language: this.language,
					insurerQuestionnaireId: this.insurer.questionnaireId,
				})
			);
		}
	}
}
